import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { authService } from "../../services";
import { brandingOf } from "../../config/branding";
import BrandMark from "../../components/BrandMark";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Button, Input, PhoneInput } from "../../components/ui";
import Icon from "../../components/ui/Icon";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// สมัครฝั่งลูกค้า — เข้าได้ทางลิงก์ของร้านเท่านั้น (/auth/register/customer?shop=CODE)
// ไม่มีรายชื่อร้านให้เลือก ร้านไหนไม่ส่งลิงก์มา ลูกค้าสมัครเข้าร้านนั้นไม่ได้
export default function RegisterCustomer() {
  const navigate = useNavigate();
  const params = useParams();
  const [search] = useSearchParams();
  const { toast } = useApp();
  const { t, tx } = useI18n();

  const shopCode = (params.shopCode || search.get("shop") || "").toUpperCase();

  const [shop, setShop] = useState(null);
  const [shopError, setShopError] = useState("");
  const [checking, setChecking] = useState(true);

  const [form, setForm] = useState({ fullname: "", phone: "", email: "", address: "", password: "", confirmPassword: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!shopCode) {
      setShopError(tx("ลิงก์ไม่ถูกต้อง — ต้องเปิดจากลิงก์สมัครที่ร้านส่งให้", "Invalid link — open the sign-up link your shop sent you"));
      setChecking(false);
      return () => {};
    }

    authService
      .shopByCode(shopCode)
      .then((row) => {
        if (!alive) return;
        if (!row) {
          setShopError(tx(`ไม่พบร้านรหัส ${shopCode}`, `Shop ${shopCode} not found`));
          return;
        }
        // ร้านชุดฟรี (ยังไม่ซื้อ/หมดอายุ) ที่ลูกค้าเต็มโควตา รับสมัครเพิ่มไม่ได้
        if (row.signupFull) {
          setShopError(tx(`${row.name} รับสมาชิกเต็มแล้ว กรุณาติดต่อร้านโดยตรง`, `${row.name} is not accepting new members — please contact the shop`));
          return;
        }
        setShop(row);
      })
      .catch((err) => alive && setShopError(err.message))
      .finally(() => alive && setChecking(false));

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopCode]);

  const onChange = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const validate = () => {
    if (!form.fullname) return tx("กรุณากรอกชื่อ-นามสกุล", "Please enter your name");
    if (form.phone.length !== 10) return tx("เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลัก", "Phone must be 10 digits");
    if (!EMAIL_PATTERN.test(form.email)) return tx("รูปแบบอีเมลไม่ถูกต้อง", "Invalid email format");
    if (form.password.length < 6) return tx("รหัสผ่านต้องมี 6 ตัวอักษรขึ้นไป", "Password must be at least 6 characters");
    if (form.password !== form.confirmPassword) return tx("รหัสผ่านไม่ตรงกัน", "Passwords do not match");
    return "";
  };

  const submit = async (event) => {
    event.preventDefault();
    const message = validate();
    setError(message);
    if (message) return;

    setLoading(true);
    try {
      await authService.registerCustomer({ ...form, shopCode: shop.code });
      toast(tx("สมัครสมาชิกสำเร็จ กรุณาเข้าสู่ระบบ", "Account created, please log in"), "success");
      navigate("/auth/login");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return <p className="u-muted">{tx("กำลังตรวจสอบลิงก์ร้าน...", "Checking shop link...")}</p>;
  }

  if (!shop) {
    return (
      <>
        <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>{tx("สมัครสมาชิกไม่ได้", "Cannot sign up")}</h1>
        <div className="field-error u-mb-16">{shopError}</div>
        <p className="u-fs-13 u-muted u-mb-16">
          {tx(
            "ลูกค้าต้องสมัครผ่านลิงก์ของร้านเท่านั้น กรุณาขอลิงก์จากร้านที่คุณใช้บริการ",
            "Customers can only sign up through their shop's link. Please ask your shop for the link."
          )}
        </p>
        <Link to="/auth/login">{t("action.login")}</Link>
      </>
    );
  }

  const brand = brandingOf(shop);

  return (
    <>
      <div className="card-crm u-mb-16" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {brand.custom ? (
          <BrandMark brand={brand} size={36} />
        ) : (
          <span style={{ fontSize: 20, color: shop.logoColor || "var(--c-indigo-500)" }}><Icon name="store" /></span>
        )}
        <div>
          <div className="u-fs-13 u-muted">{tx("สมัครเป็นลูกค้าของ", "Signing up with")}</div>
          <div className="u-strong">{brand.custom ? brand.name : shop.name}</div>
        </div>
      </div>

      <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>{t("auth.registerTitle")}</h1>
      <p className="u-muted u-mb-16">
        {tx("บัญชีนี้ใช้ได้กับร้านนี้เท่านั้น", "This account works with this shop only")}
      </p>

      <form onSubmit={submit}>
        <Input label={tx("ชื่อ-นามสกุล", "Full name")} value={form.fullname} onChange={onChange("fullname")} />
        <PhoneInput label={t("common.phone")} value={form.phone} onChange={(value) => setForm((prev) => ({ ...prev, phone: value }))} />
        <Input label={t("common.email")} type="email" value={form.email} onChange={onChange("email")} />
        <Input label={tx("ที่อยู่", "Address")} value={form.address} onChange={onChange("address")} />
        <Input label={t("auth.password")} type="password" value={form.password} onChange={onChange("password")} autoComplete="new-password" />
        <Input label={t("auth.confirmPassword")} type="password" value={form.confirmPassword} onChange={onChange("confirmPassword")} autoComplete="new-password" />

        {error ? <div className="field-error u-mb-8">{error}</div> : null}

        <Button type="submit" variant="primary" block disabled={loading}>
          {loading ? tx("กำลังสมัคร...", "Creating...") : t("action.register")}
        </Button>
      </form>

      <div className="u-center u-mt-16 u-fs-13">
        {t("auth.hasAccount")} <Link to="/auth/login">{t("action.login")}</Link>
      </div>
    </>
  );
}
